import { Status, Wrapper } from "@googlemaps/react-wrapper";
import { LocationOn as LocationIcon } from "@mui/icons-material";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { GOOGLE_API_KEY } from "config";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const render = status => {
  switch (status) {
    case Status.LOADING:
      return (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <CircularProgress />
        </Box>
      );
    case Status.FAILURE:
      return (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "grey.100",
            borderRadius: "8px"
          }}
        >
          <Stack alignItems="center" spacing={1}>
            <LocationIcon sx={{ fontSize: 48, color: "grey.400" }} />
            <Typography variant="body2" color="grey.600">
              Failed to load map
            </Typography>
          </Stack>
        </Box>
      );
    default:
      return null;
  }
};

const MapComponent = ({ center, zoom = 15, markers = [], height = 300 }) => {
  const ref = useRef(null);
  const [map, setMap] = useState(null);
  const markersArrayRef = useRef([]);
  const [lastMarkerUpdate, setLastMarkerUpdate] = useState("");

  useEffect(() => {
    if (ref.current && !map) {
      const newMap = new window.google.maps.Map(ref.current, {
        center,
        zoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: false,
        gestureHandling: "cooperative"
      });
      setMap(newMap);
    }
  }, [ref, map]);

  // Update map center and zoom when they change
  useEffect(() => {
    if (map && center) {
      map.setCenter(center);
      map.setZoom(zoom);
    }
  }, [map, center, zoom]);

  // Update markers only when markers data actually changes
  useEffect(() => {
    if (!map) return;

    const markersKey = JSON.stringify(markers);
    if (markersKey === lastMarkerUpdate) return;

    // Clear existing markers
    markersArrayRef.current.forEach(marker => marker.setMap(null));
    markersArrayRef.current = [];

    // Add new markers
    const newMarkers = markers.map(markerData => {
      const marker = new window.google.maps.Marker({
        position: markerData.position,
        map,
        title: markerData.title
      });

      if (markerData.infoWindow) {
        const infoWindow = new window.google.maps.InfoWindow({
          content: markerData.infoWindow
        });

        marker.addListener("click", () => {
          infoWindow.open(map, marker);
        });
      }

      return marker;
    });

    markersArrayRef.current = newMarkers;
    setLastMarkerUpdate(markersKey);

    // Auto-fit bounds if multiple markers
    if (markers.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach(marker => bounds.extend(marker.position));
      map.fitBounds(bounds);
    }
  }, [map, markers, lastMarkerUpdate]);

  return (
    <Box
      ref={ref}
      sx={{
        height: height,
        width: "100%",
        borderRadius: "8px",
        overflow: "hidden"
      }}
    />
  );
};

export const GoogleMap = ({
  address,
  churchName,
  height = 300,
  zoom = 15,
  apiKey = GOOGLE_API_KEY || ""
}) => {
  const [center, setCenter] = useState({ lat: 9.0306, lng: 38.7406 }); // Default to Addis Ababa
  const [markers, setMarkers] = useState([]);
  const [geocoding, setGeocoding] = useState(false);
  const [lastGeocodedAddress, setLastGeocodedAddress] = useState("");

  const waitForGoogleMaps = useCallback(async (maxAttempts = 10) => {
    for (let i = 0; i < maxAttempts; i++) {
      if (window.google?.maps?.Geocoder) {
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    return false;
  }, []);

  const geocodeAddress = useCallback(async () => {
    if (!address || address === lastGeocodedAddress) return;

    setGeocoding(true);

    try {
      // Wait for Google Maps API to be available
      const isApiReady = await waitForGoogleMaps();
      if (!isApiReady) {
        throw new Error("Google Maps API not loaded");
      }

      const geocoder = new window.google.maps.Geocoder();

      const result = await new Promise((resolve, reject) => {
        geocoder.geocode({ address }, (results, status) => {
          if (status === "OK" && results && results.length > 0) {
            resolve(results[0]);
          } else {
            reject(new Error(`Geocoding failed with status: ${status}`));
          }
        });
      });

      const location = result.geometry.location;
      const newCenter = {
        lat: location.lat(),
        lng: location.lng()
      };

      setCenter(newCenter);
      setMarkers([
        {
          position: newCenter,
          title: churchName || "Church Location",
          infoWindow: `
            <div style="padding: 8px;">
              <h3 style="margin: 0 0 8px 0; font-size: 16px;">${
                churchName || "Church Location"
              }</h3>
              <p style="margin: 0; font-size: 14px; color: #666;">${address}</p>
            </div>
          `
        }
      ]);
      setLastGeocodedAddress(address);
    } catch (error) {
      // Keep default center and show marker there with limited info
      const defaultCenter = { lat: 9.0306, lng: 38.7406 };
      setCenter(defaultCenter);
      setMarkers([
        {
          position: defaultCenter,
          title: churchName || "Church Location",
          infoWindow: `
            <div style="padding: 8px;">
              <h3 style="margin: 0 0 8px 0; font-size: 16px;">${
                churchName || "Church Location"
              }</h3>
              <p style="margin: 0; font-size: 14px; color: #666;">Could not find location: "${address}"</p>
            </div>
          `
        }
      ]);
      setLastGeocodedAddress(address);
    } finally {
      setGeocoding(false);
    }
  }, [address, churchName, lastGeocodedAddress, waitForGoogleMaps]);

  useEffect(() => {
    if (address && address.trim()) {
      geocodeAddress();
    } else if (churchName && (!address || !address.trim())) {
      // Show marker at default location if no address but church name exists
      const defaultCenter = { lat: 9.0306, lng: 38.7406 };
      setCenter(defaultCenter);
      setMarkers([
        {
          position: defaultCenter,
          title: churchName,
          infoWindow: `
            <div style="padding: 8px;">
              <h3 style="margin: 0 0 8px 0; font-size: 16px;">${churchName}</h3>
              <p style="margin: 0; font-size: 14px; color: #666;">No specific location provided</p>
            </div>
          `
        }
      ]);
    } else if (!address && !churchName) {
      // Clear markers if no data
      setMarkers([]);
    }
  }, [address, churchName, geocodeAddress]);

  if (!apiKey) {
    return (
      <Box
        sx={{
          height,
          bgcolor: "grey.100",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Stack alignItems="center" spacing={1}>
          <LocationIcon sx={{ fontSize: 48, color: "grey.400" }} />
          <Typography variant="body2" color="grey.600">
            Google Maps API key not configured
          </Typography>
        </Stack>
      </Box>
    );
  }

  const memoizedMapProps = useMemo(
    () => ({
      center,
      zoom,
      markers,
      height
    }),
    [center, zoom, markers, height]
  );

  return (
    <Box sx={{ position: "relative" }}>
      <Wrapper apiKey={apiKey} render={render} libraries={["places"]}>
        <MapComponent {...memoizedMapProps} />
      </Wrapper>
      {geocoding && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            bgcolor: "background.paper",
            borderRadius: 1,
            p: 1,
            boxShadow: 1,
            display: "flex",
            alignItems: "center",
            gap: 1
          }}
        >
          <CircularProgress size={16} />
          <Typography variant="caption">Loading location...</Typography>
        </Box>
      )}
    </Box>
  );
};
